/**
 * GranulloIntelligenceContext.jsx — THE BRAIN
 *
 * FOUNDER DIRECTIVE: SINGLE SOURCE OF TRUTH
 *
 * ONE program state.
 * ONE condition engine.
 * ONE evaluation logic.
 *
 * Used by: Simulator, Timeline, Proof Stream, Explainer.
 * Persists across all sections. Does NOT reset on scroll.
 * Does NOT live inside any component.
 *
 * This is Granullo's intelligence layer.
 */
import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';
import { createExecutionProgram, evaluateExecution, mapEvaluationToUI } from '../lib/programApi';

/* ═══════════════════════ COLOR SYSTEM ═══════════════════════ */
export const COLORS = {
  allowed: '#B8D7F0',
  blocked: '#D7D2EA',
  pending: '#9CC8E8',
  system: '#9CC8E8',
  intelligence: '#B8D7F0',
};

/* ═══════════════════════ PROGRAM TEMPLATES ═══════════════════════ */
export const CHIPS = [
  { label: 'Employee Benefits', icon: 'Users', text: '$500 employee wellness benefit, gym and pharmacy only, within 30 days' },
  { label: 'Vendor Payment', icon: 'Truck', text: '$15,000 vendor payment after delivery confirmation and quality inspection' },
  { label: 'Grant Funding', icon: 'GraduationCap', text: '$25,000 research grant, lab equipment and supplies only, after milestone review' },
  { label: 'CSR Disbursement', icon: 'Heart', text: '$10,000 CSR fund for community projects, verified NGOs only, within 90 days' },
  { label: 'Startup Funding', icon: 'Rocket', text: '$50,000 startup funding, approved vendors only, after board approval' },
  { label: 'Climate Incentive', icon: 'Leaf', text: '$2,000 climate incentive for solar equipment, certified installers only' },
  { label: 'Insurance Payout', icon: 'Shield', text: '$3,000 insurance payout for home repairs only, within 60 days' },
  { label: 'Government Subsidy', icon: 'Landmark', text: '\u20B910,000 agricultural subsidy for seeds, only at verified suppliers, within 90 days' },
  { label: 'Milestone Payment', icon: 'Target', text: '$100,000 milestone payment after evidence submission and audit clearance' },
];

/* ═══════════════════════ MERCHANT TEST SCENARIOS ═══════════════════════ */
export const MERCHANT_TESTS = {
  'Employee Benefit': [
    { name: 'Gym membership', amount: 80, allowed: true, category: 'gym' },
    { name: 'Pharmacy', amount: 45, allowed: true, category: 'pharmacy' },
    { name: 'Electronics store', amount: 200, allowed: false, category: 'electronics' },
    { name: 'Restaurant', amount: 50, allowed: false, category: 'dining' },
  ],
  'Vendor Payment': [
    { name: 'Verified vendor', amount: 5000, allowed: true, category: 'verified_vendor' },
    { name: 'Approved supplier', amount: 3000, allowed: true, category: 'approved_supplier' },
    { name: 'Personal transfer', amount: 5000, allowed: false, category: 'personal' },
    { name: 'Unverified account', amount: 2000, allowed: false, category: 'unverified' },
  ],
  'Grant Disbursement': [
    { name: 'Lab equipment', amount: 8000, allowed: true, category: 'research_equipment' },
    { name: 'Research supplies', amount: 2500, allowed: true, category: 'research_supplies' },
    { name: 'Personal expense', amount: 1000, allowed: false, category: 'personal' },
    { name: 'Unapproved travel', amount: 3000, allowed: false, category: 'travel' },
  ],
  'Government Subsidy': [
    { name: 'Verified seed supplier', amount: 3000, allowed: true, category: 'seeds' },
    { name: 'Agricultural co-op', amount: 2000, allowed: true, category: 'verified_suppliers' },
    { name: 'Electronics store', amount: 1500, allowed: false, category: 'electronics' },
    { name: 'Cash withdrawal', amount: 5000, allowed: false, category: 'cash' },
  ],
  'Insurance Payout': [
    { name: 'Home repair service', amount: 1200, allowed: true, category: 'home_repair' },
    { name: 'Licensed contractor', amount: 800, allowed: true, category: 'licensed_contractor' },
    { name: 'Luxury goods', amount: 2000, allowed: false, category: 'luxury' },
    { name: 'Travel agency', amount: 500, allowed: false, category: 'travel' },
  ],
  'Controlled Payment': [
    { name: 'Approved vendor', amount: 500, allowed: true, category: 'approved_vendor' },
    { name: 'Verified supplier', amount: 300, allowed: true, category: 'verified_supplier' },
    { name: 'Restricted merchant', amount: 200, allowed: false, category: 'restricted' },
    { name: 'Cash withdrawal', amount: 1000, allowed: false, category: 'cash' },
  ],
  'Controlled Disbursement': [
    { name: 'Approved vendor', amount: 200, allowed: true, category: 'approved_vendor' },
    { name: 'Verified store', amount: 100, allowed: true, category: 'verified_store' },
    { name: 'Online gaming', amount: 50, allowed: false, category: 'gaming' },
    { name: 'Cash withdrawal', amount: 300, allowed: false, category: 'cash' },
  ],
};

/* ═══════════════════════ CORE ENGINE: PARSER ═══════════════════════ */
export function parseProgram(text) {
  if (!text || text.trim().length < 8) return null;
  const t = text.toLowerCase();
  const amountMatch = text.match(/[$\u20B9\u20AC\u00A3]\s?[\d,]+(?:\.\d+)?[KMBT]?\+?/i);
  const amount = amountMatch ? amountMatch[0].replace(/\s/g, '') : null;
  const timeMatch = t.match(/within\s+(\d+)\s*(days?|months?|weeks?)/i);
  const timelineDays = timeMatch ? parseInt(timeMatch[1]) : null;
  const timelineStr = timeMatch ? `${timeMatch[1]} ${timeMatch[2]}` : null;
  const conditions = [];
  const afterParts = t.split(/after\s+/);
  if (afterParts.length > 1) {
    for (let i = 1; i < afterParts.length; i++) {
      const condSegments = afterParts[i].split(/[,.]/)[0].split(/\s+and\s+/);
      condSegments.forEach(seg => { const c = seg.trim(); if (c) conditions.push(c.charAt(0).toUpperCase() + c.slice(1)); });
    }
  }
  const usage = [];
  const forMatch = t.match(/for\s+(.+?)(?:,|\s+only|\s+within|\s+after|$)/);
  if (forMatch) usage.push(forMatch[1].trim());
  const onlyAtMatch = t.match(/(?:only\s+at|at)\s+(.+?)(?:,|\s+within|\s+after|$)/);
  if (onlyAtMatch) usage.push(onlyAtMatch[1].trim());
  const xOnlyMatch = t.match(/,\s+(.+?)\s+only(?:\s|,|$)/);
  if (xOnlyMatch && !xOnlyMatch[1].includes('at') && !xOnlyMatch[1].includes('after')) usage.push(xOnlyMatch[1].trim());
  let type = 'Controlled Payment';
  if (t.includes('benefit') || t.includes('wellness')) type = 'Employee Benefit';
  else if (t.includes('subsidy')) type = 'Government Subsidy';
  else if (t.includes('insurance') || t.includes('payout') || t.includes('claim')) type = 'Insurance Payout';
  else if (t.includes('vendor')) type = 'Vendor Payment';
  else if (t.includes('grant') || t.includes('research')) type = 'Grant Disbursement';
  else if (t.includes('groceries') || t.includes('food')) type = 'Controlled Disbursement';
  return { amount: amount || '\u2014', type, allowedUsage: usage.length > 0 ? usage : ['As specified'], timeline: timelineStr, timelineDays, conditions };
}

/* ═══════════════════════ CORE ENGINE: BENEFICIARY ═══════════════════════ */
export function inferBeneficiary(type) {
  const map = {
    'Employee Benefit': 'Employee',
    'Government Subsidy': 'Citizen',
    'Insurance Payout': 'Policyholder',
    'Vendor Payment': 'Vendor',
    'Grant Disbursement': 'Researcher',
    'Controlled Disbursement': 'Designated Beneficiary',
    'Controlled Payment': 'Designated Beneficiary',
  };
  return map[type] || 'Beneficiary';
}

/* ═══════════════════════ CORE ENGINE: CONDITION CHECKS ═══════════════════════ */
export function generateChecks(parsed, merchant) {
  const checks = [];
  checks.push({ text: merchant.allowed ? 'Merchant verified' : 'Merchant not in allowed category', pass: merchant.allowed });
  const rawAmt = (parsed.amount || '0').toUpperCase();
  const numericVal = parseFloat(rawAmt.replace(/[^0-9.]/g, '')) || 0;
  const suffix = rawAmt.match(/[KMBT]/) ? rawAmt.match(/[KMBT]/)[0] : '';
  const multiplier = suffix === 'K' ? 1e3 : suffix === 'M' ? 1e6 : suffix === 'B' ? 1e9 : suffix === 'T' ? 1e12 : 1;
  const amt = numericVal > 0 ? numericVal * multiplier : Infinity;
  checks.push({ text: merchant.amount <= amt ? 'Amount within limit' : 'Amount exceeds program limit', pass: merchant.amount <= amt });
  if (parsed.type === 'Employee Benefit') checks.push({ text: 'Employment status active', pass: true });
  else if (parsed.type === 'Vendor Payment') checks.push({ text: 'Delivery confirmation', pass: parsed.conditions.length === 0 });
  else if (parsed.type === 'Grant Disbursement') checks.push({ text: 'Milestone review complete', pass: parsed.conditions.length === 0 });
  else if (parsed.type === 'Government Subsidy') checks.push({ text: 'Supplier verification', pass: true });
  else if (parsed.type === 'Insurance Payout') checks.push({ text: 'Claim validated', pass: true });
  else checks.push({ text: 'Program rules verified', pass: true });
  if (parsed.conditions.length > 0) checks.push({ text: parsed.conditions[0], pass: false });
  else if (parsed.timeline) checks.push({ text: 'Within allowed timeframe', pass: true });
  return checks;
}

/* ═══════════════════════ CORE ENGINE: FRICTION INTELLIGENCE ═══════════════════════ */
export function detectNudge(text) {
  if (!text || text.length < 18) return null;
  const t = text.toLowerCase();
  if (t.includes('after delivery') && !t.match(/delivery\s+(confirmation|verified|proof)/)) return 'What confirms delivery?';
  if (t.includes('after approval') && !t.match(/(who|board|manager|finance)\s+approv/)) return 'Who approves?';
  if (!t.match(/[$\u20B9\u20AC\u00A3]/) && t.length > 28) return 'How much per transaction?';
  if (t.match(/[$\u20B9\u20AC\u00A3]\s?[\d,]+/) && !t.match(/(only|for\s|at\s)/) && t.length > 15) return 'Where can this be spent?';
  if (t.length > 40 && !t.match(/(within|before|after|by\s)/)) return 'When should this expire?';
  return null;
}

/* ═══════════════════════ CORE ENGINE: EVALUATE TRANSACTION ═══════════════════════ */
export function evaluateTransaction(parsed, merchant) {
  if (!parsed) return null;
  const checks = generateChecks(parsed, merchant);
  const allowed = checks.every(c => c.pass);
  return {
    allowed,
    checks,
    reason: checks.find(c => !c.pass)?.text || '',
    merchant: merchant.name,
    amount: merchant.amount,
    programType: parsed.type,
    timestamp: Date.now(),
  };
}

/* ═══════════════════════ CONTEXT ═══════════════════════ */
const GranulloIntelligenceContext = createContext(null);

export function GranulloIntelligenceProvider({ children }) {
  /* ── Program State (persists across all sections) ── */
  const [programText, setProgramText] = useState('');
  const [selectedChipIndex, setSelectedChipIndex] = useState(null);
  const [programSource, setProgramSource] = useState(null); // 'chip' | 'input' | 'voice' | 'timeline' | 'proof'

  /* ── Execution History (shared across simulator, proof stream, timeline) ── */
  const [executionLog, setExecutionLog] = useState([]);

  /* ── Break-the-System tracking ── */
  const [consecutiveBlocked, setConsecutiveBlocked] = useState(0);
  const [totalTests, setTotalTests] = useState(0);

  /* ── Chip Memory (localStorage) ── */
  const [rememberedChip, setRememberedChip] = useState(() => {
    try { return localStorage.getItem('granullo_last_chip'); } catch { return null; }
  });

  /* ── Execution Engine: real backend program ── */
  const executionProgramRef = useRef(null);
  const executionProgramTextRef = useRef('');
  const creatingProgramRef = useRef(false);

  /* ── Derived: parsed program (single computation) ── */
  const parsedProgram = useMemo(() => parseProgram(programText), [programText]);

  /* ── Derived: system status ── */
  const systemStatus = useMemo(() => {
    if (!programText || programText.length < 5) return '';
    const statuses = [
      { t: 8, s: 'Identifying participants\u2026' },
      { t: 20, s: 'Detecting conditions\u2026' },
      { t: 35, s: 'Resolving constraints\u2026' },
      { t: 50, s: 'Structuring program\u2026' },
    ];
    return statuses.filter(s => programText.length >= s.t).pop()?.s || '';
  }, [programText]);

  /* ── Derived: friction nudge ── */
  const nudge = useMemo(() => detectNudge(programText), [programText]);

  /* ── Derived: intensity (how much the user has typed) ── */
  const intensity = useMemo(() => Math.min(1, (programText?.length || 0) / 60), [programText]);

  /* ── Derived: merchant tests for current program type ── */
  const merchantTests = useMemo(() => {
    if (!parsedProgram) return [];
    return MERCHANT_TESTS[parsedProgram.type] || MERCHANT_TESTS['Controlled Payment'];
  }, [parsedProgram]);

  /* ── Derived: preview ID ── */
  const previewId = useMemo(() => {
    if (!parsedProgram) return '';
    const h = programText.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
    return 'PRG-' + Math.abs(h).toString(16).toUpperCase().slice(0, 8).padStart(8, '0');
  }, [programText, parsedProgram]);

  /* ── Derived: system confidence (evolves with usage) ── */
  const confidence = useMemo(() => {
    if (!parsedProgram) return 0;
    const base = 0.3;
    const fromLength = Math.min(0.3, programText.length / 100);
    const fromTests = Math.min(0.4, totalTests * 0.1);
    return Math.min(1, base + fromLength + fromTests);
  }, [parsedProgram, programText, totalTests]);

  /* ── Derived: break-the-system messages ── */
  const breakMessage = useMemo(() => {
    if (consecutiveBlocked <= 0) return null;
    const msgs = ['Still holds.', 'Condition enforced.', 'No path found.', 'Structurally blocked.', 'Immovable.'];
    return msgs[Math.min(consecutiveBlocked - 1, msgs.length - 1)];
  }, [consecutiveBlocked]);

  /* ── Derived: has blocking conditions ── */
  const hasConditions = parsedProgram && parsedProgram.conditions.length > 0;

  /* ═══ ACTIONS ═══ */

  /** Inject a program from ANY section. Single entry point. */
  const injectProgram = useCallback((text, source = 'input', chipIdx = null) => {
    setProgramText(text);
    setProgramSource(source);
    setSelectedChipIndex(chipIdx);
    if (source === 'chip' && chipIdx !== null) {
      const chip = CHIPS[chipIdx];
      if (chip) {
        try { localStorage.setItem('granullo_last_chip', chip.label); } catch {}
        setRememberedChip(chip.label);
      }
    }
  }, []);

  /** Evaluate a transaction against the current program. Tries real backend first, falls back to mock. */
  const evaluate = useCallback((merchant) => {
    if (!parsedProgram) return null;
    // Synchronous mock evaluation (instant result for UI)
    const result = evaluateTransaction(parsedProgram, merchant);
    if (result) {
      setExecutionLog(prev => [result, ...prev].slice(0, 50));
      setTotalTests(prev => prev + 1);
      setConsecutiveBlocked(prev => result.allowed ? 0 : prev + 1);
    }
    return result;
  }, [parsedProgram]);

  /** Async real evaluation via the Execution Engine backend.
   *  Returns { result, real } where real=true means backend responded.
   *  Graceful: never throws, never breaks UI. Falls back to mock. */
  const evaluateAsync = useCallback(async (merchant) => {
    if (!parsedProgram) return { result: null, real: false };

    // Ensure we have a real execution program
    let execProgram = executionProgramRef.current;
    const currentText = programText;

    if (!execProgram || executionProgramTextRef.current !== currentText) {
      if (!creatingProgramRef.current) {
        creatingProgramRef.current = true;
        try {
          execProgram = await createExecutionProgram(parsedProgram, currentText);
          if (execProgram) {
            executionProgramRef.current = execProgram;
            executionProgramTextRef.current = currentText;
          }
        } catch {
          execProgram = null;
        }
        creatingProgramRef.current = false;
      }
    }

    // Try real evaluation
    if (execProgram && execProgram.program_id) {
      try {
        const evalResult = await evaluateExecution(execProgram.program_id, merchant);
        if (evalResult) {
          const uiResult = mapEvaluationToUI(evalResult, merchant);
          if (uiResult) {
            setExecutionLog(prev => [uiResult, ...prev].slice(0, 50));
            setTotalTests(prev => prev + 1);
            setConsecutiveBlocked(prev => uiResult.allowed ? 0 : prev + 1);
            return { result: uiResult, real: true };
          }
        }
      } catch {
        // Fall through to mock
      }
    }

    // Fallback: mock evaluation
    const mockResult = evaluateTransaction(parsedProgram, merchant);
    if (mockResult) {
      setExecutionLog(prev => [mockResult, ...prev].slice(0, 50));
      setTotalTests(prev => prev + 1);
      setConsecutiveBlocked(prev => mockResult.allowed ? 0 : prev + 1);
    }
    return { result: mockResult, real: false };
  }, [parsedProgram, programText]);

  /** Evaluate without logging (for previews, timeline simulations) */
  const evaluatePreview = useCallback((programTextOverride, merchant) => {
    const parsed = parseProgram(programTextOverride);
    if (!parsed) return null;
    return evaluateTransaction(parsed, merchant);
  }, []);

  /** Clear chip memory */
  const clearChipMemory = useCallback(() => {
    try { localStorage.removeItem('granullo_last_chip'); } catch {}
    setRememberedChip(null);
  }, []);

  /** Reset entire system state */
  const resetSystem = useCallback(() => {
    setProgramText('');
    setSelectedChipIndex(null);
    setProgramSource(null);
  }, []);

  /* ═══ CONTEXT VALUE ═══ */
  const value = useMemo(() => ({
    // State
    programText,
    parsedProgram,
    selectedChipIndex,
    programSource,
    rememberedChip,
    executionLog,
    consecutiveBlocked,
    totalTests,

    // Derived signals
    systemStatus,
    nudge,
    intensity,
    merchantTests,
    previewId,
    hasConditions,
    confidence,
    breakMessage,

    // Actions
    injectProgram,
    evaluate,
    evaluateAsync,
    evaluatePreview,
    clearChipMemory,
    resetSystem,
  }), [
    programText, parsedProgram, selectedChipIndex, programSource,
    rememberedChip, executionLog, consecutiveBlocked, totalTests,
    systemStatus, nudge, intensity, merchantTests, previewId,
    hasConditions, confidence, breakMessage,
    injectProgram, evaluate, evaluateAsync, evaluatePreview, clearChipMemory, resetSystem,
  ]);

  return (
    <GranulloIntelligenceContext.Provider value={value}>
      {children}
    </GranulloIntelligenceContext.Provider>
  );
}

export function useGranulloIntelligence() {
  const ctx = useContext(GranulloIntelligenceContext);
  if (!ctx) throw new Error('useGranulloIntelligence must be used within GranulloIntelligenceProvider');
  return ctx;
}

export default GranulloIntelligenceContext;
